import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";
import { useState, useEffect } from "react";

import {
  Button,
  ApplicantExamHeader,
  ApplicantChallengeList,
  CustomFormInput,
  Footer,
  Icon,
  Markdown,
  FormGroup,
  Checkbox,
  Modal,
  Msg,
  Banner,
} from "@shared/components";
import { useFeatureFlags } from "@shared/hooks/query";
import { ApplicantExamModel, HealthCheckModel } from "@shared/models";
import {
  formatTimeLeftString,
  formatDateTimeMinutes,
  getTimeZoneString,
} from "@shared/services/date";
import {
  ExamType,
  GlobalAppType,
  WebcamSettings,
} from "@shared/services/enums";
import * as ExamService from "@shared/services/exam";
import MSG from "@shared/services/message";

import SystemHealth from "../../../../common/systemHealth/SystemHealth.connect";
import { ApplicantSegmentSurveyForm } from "../applicantSegmentSurvey/ApplicantSegmentSurveyForm";
import { HelpSection } from "../helpSection/HelpSection";
import { isFormValidCheck, shouldShowTimeLimit } from "./components/utils";
import { ModalSteps } from "./modalSteps/ModalSteps";
import { hasFaceVerified, hasRefusalReason } from "./utils";

/**
 * Prop interface
 */
export interface EditorExamDetailProps {
  exam: ApplicantExamModel;
  health: HealthCheckModel;
  tutorial?: boolean;

  updateFormAndOpenExam: (payload: {}) => void;
  updateForm: (payload: {}) => void;
  submitExam: () => void;
  webcamFeatureFlagEnabled?: boolean;
}

/**
 * State interface
 */
export interface EditorExamDetailState {
  isOpenHealth: boolean;
  isOpenStartExam: boolean;
  fullname: string;
  formValid: boolean;
  formValues: {};
  formErrors?: {};
  isTermAccepted: boolean;
  hasAttemptedSubmit: boolean;
  applicantSegment: {
    formValid: boolean;
    formValues: {};
    formErrors?: {};
  };
}

const EditorExamDetailFunctional: React.FC<EditorExamDetailProps> = (props) => {
  const [isOpenHealth, setIsOpenHealth] = useState(false);
  const [isOpenStartExam, setIsOpenStartExam] = useState(false);
  const [fullname, setFullname] = useState("");
  const [formValid, setFormValid] = useState(!props.exam.hasEntryForm());
  const [formValues, setFormValues] = useState({});
  const [isTermAccepted, setIsTermAccepted] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [applicantSegment, setApplicantSegment] = useState<{
    formValid: boolean;
    formValues: {};
    formErrors?: {};
  }>({
    formValid: false,
    formValues: {},
    formErrors: {},
  });
  const featureFlags = useFeatureFlags();
  const webcamFeatureFlagEnabled = featureFlags.data?.webcamEnabled
    ? true
    : false;
  const { exam, health, tutorial } = props;
  const webcamEnabled =
    Boolean(webcamFeatureFlagEnabled) && props.exam.webcamSettings.enabled;
  const hasReason = hasRefusalReason(
    webcamFeatureFlagEnabled,
    props.exam.webcamStatus.hasSavedRefusalReason,
  );

  const faceVerified = hasFaceVerified(
    webcamFeatureFlagEnabled,
    props.exam.webcamStatus.hasVerifiedLiveness,
  );

  // Mimic componentDidMount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // Runs once on mount

  const { deliveryMethod, deadline } = exam;
  const rootStyle = classnames("code-editor-exam-detail");

  const multiLangage = exam.exam.examType === ExamType.EnglishJapanese;
  const hasCodingChallenge = ExamService.hasCodingChallenge(exam);

  const onOpenStartExam = () => {
    if (webcamEnabled) {
      if (!formValid || !applicantSegment.formValid) {
        setHasAttemptedSubmit(true);
        return;
      }
      if (hasReason) {
        onSaveAndStartExam();
      } else {
        setIsOpenStartExam(true);
      }
    } else {
      if (!isTermAccepted || !formValid || !applicantSegment.formValid) {
        setHasAttemptedSubmit(true);
        return;
      }

      setIsOpenStartExam(true);
    }
  };

  const onCloseModal = () => {
    setIsOpenStartExam(false);
    setIsOpenHealth(false);
  };

  const onSaveAndStartExam = () => {
    const applicationSegmentValues = applicantSegment.formValues;
    const payload = {
      fullname,
      values: formValues,
      applicantSegment: applicationSegmentValues,
    };
    props.updateFormAndOpenExam(payload);
  };

  const onUpdateEntryForm = () => {
    if (!formValid || !applicantSegment.formValid) {
      setHasAttemptedSubmit(true);
      return;
    } else {
      setHasAttemptedSubmit(false);
    }

    const values = formValues;

    const payload = {
      fullname,
      ...{ values },
      applicantSegment: applicantSegment.formValues,
    };
    props.updateForm(payload);
  };

  const onOpenHealth = () => {
    setIsOpenHealth(true);
  };

  const onAgreementChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { checked } = event.target as HTMLInputElement;
    setIsTermAccepted(checked);
  };

  const onFormChange = (
    formValid: boolean,
    formValues: {},
    formErrors?: {},
  ) => {
    const { fullname, ...rest } = formValues as { fullname: string };
    setFullname(fullname);
    setFormValid(formValid || !Boolean(formErrors));
    setFormValues(rest);
  };

  const onApplicantSegmentFormChange = (
    formValid: boolean,
    formValues: {},
    formErrors?: {},
  ) => {
    setApplicantSegment({
      formValid: formValid || !Boolean(formErrors),
      formValues,
      formErrors,
    });
  };

  const deliveryTestWarning = (
    <div className="code-editor-exam-detail__sample-delivery-note">
      <Msg id={"delivery.test.warning"} />
    </div>
  );

  const tutorialTestWarning = (
    <div className="code-editor-exam-detail__sample-delivery-note">
      <Msg id={"tutorial.test.warning"} />
    </div>
  );

  const envCheckStyle = classnames("code-editor-exam-detail__environment", {
    "all-green": props.health.passed(),
  });

  const examEnvironmentCheck = (
    <div className={envCheckStyle}>
      <div className="code-editor-exam-detail__environment-container">
        <div className="code-editor-exam-detail__environment-left">
          <Icon
            className="code-editor-exam-detail__environment-icon"
            type={health.passed() ? "check-circle" : "times-circle"}
          />
          <strong>
            {health.passed() ? (
              <Msg id="editor-exam-detail.exam.systemAllGood" />
            ) : (
              <Msg id="editor-exam-detail.exam.systemHadIssues" />
            )}
          </strong>
        </div>
        <div>
          <Button
            size="small"
            shrink={true}
            onClick={onOpenHealth}
            ariaLabel="Open System Checker"
          >
            <Msg id={"action.seeDetails"} />
          </Button>
        </div>
      </div>
      {!health.passed() && <Msg id="editor-exam-detail.exam.technicalIssues" />}
    </div>
  );

  const showTimeLimit = shouldShowTimeLimit(
    deliveryMethod,
    exam.getTimeLimitMinutes(),
    deadline,
  );

  const isFormValid = isFormValidCheck(
    webcamEnabled,
    formValid,
    applicantSegment,
    isTermAccepted,
    exam,
  );

  const formErrorBanner =
    hasAttemptedSubmit && !isFormValid ? (
      <FormGroup className="code-editor-exam-detail__general-form-error-banner">
        <Banner type="error">
          <Msg id="validation.generalFormError" />
        </Banner>
      </FormGroup>
    ) : null;

  return (
    <div className={rootStyle}>
      {exam.isTest && deliveryTestWarning}
      {tutorial && tutorialTestWarning}
      <div className="code-editor-exam-detail__container">
        <div className="code-editor-exam-detail__main">
          <div className="code-editor-exam-detail__section">
            {exam.isExpired() && (
              <div className="code-editor-exam-detail__expired">
                <span className="stop-icon" />
                <Msg id={"editor.deadlineExpired"} />
              </div>
            )}
            <ApplicantExamHeader
              tutorial={tutorial}
              orgImageUrl={exam.org.imageUrl}
              orgName={exam.org.displayName}
              title={exam.exam.name}
            />
            <div className="code-editor-exam-detail__deadline-container">
              <p className="code-editor-exam-detail__deadline">
                <Msg
                  id="editor-exam-detail.exam.SubmitBy1"
                  values={{
                    deadline: (
                      <strong>
                        <time dateTime={deadline} aria-label="deadline">
                          {formatDateTimeMinutes(deadline)}
                        </time>
                      </strong>
                    ),
                    timezone: <small>({getTimeZoneString()})</small>,
                  }}
                />
              </p>
              {showTimeLimit && (
                <p>
                  <Msg
                    id="editor-exam-detail.exam.SubmitBy2"
                    values={{
                      days: formatTimeLeftString(
                        exam.dynamicDeadlineHours * 3600,
                      ),
                    }}
                  />
                </p>
              )}
            </div>
          </div>
          <Markdown
            className="code-editor-exam-detail__section"
            body={exam.exam.description}
          />
          <ApplicantChallengeList
            className="code-editor-exam-detail__section"
            exam={exam}
          />
          <div className="code-editor-exam-detail__section">
            <div className="code-editor-exam-detail__cautions">
              <h2>
                <Msg id={"editor-exam-detail.notes"} />
              </h2>
              <div className="code-editor-exam-detail__cautions__sub-container">
                {/* exam process */}
                <div>
                  <h3 className="code-editor-exam-detail__cautions__title">
                    <Msg id="editor-exam-detail.exam.examinationProcess.title" />
                  </h3>
                  <ul>
                    <li>
                      <Msg id="editor-exam-detail.exam.examinationProcess.text1" />
                    </li>
                    <li>
                      <Msg id="editor-exam-detail.exam.examinationProcess.text2" />
                      <br />
                      <Msg id="editor-exam-detail.exam.examinationProcess.text3" />
                    </li>
                  </ul>
                </div>
                {/* coding issues */}
                {hasCodingChallenge && (
                  <div>
                    <h3 className="code-editor-exam-detail__cautions__title">
                      <Msg id="editor-exam-detail.exam.codingIssues.title" />
                    </h3>
                    <ul>
                      <li>
                        <Msg id="editor-exam-detail.exam.codingIssues.text1" />
                      </li>
                      <li>
                        <Msg id="editor-exam-detail.exam.codingIssues.text2" />
                      </li>
                      <li>
                        <Msg id="editor-exam-detail.exam.codingIssues.text3" />
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <h2>
                  <Msg id="editor-exam-detail.exam.examinationEnv.title" />
                </h2>
                <div className="code-editor-exam-detail__cautions__environment">
                  <ul>
                    <li>
                      <Msg id="editor-exam-detail.exam.examinationEnv.text1" />
                    </li>
                    <li>
                      <Msg id="editor-exam-detail.exam.examinationEnv.text2" />
                    </li>
                  </ul>
                  {examEnvironmentCheck}
                </div>
              </div>
            </div>
          </div>
          <div className="code-editor-exam-detail__section">
            {exam.hasEntryForm() && (
              <div className="code-editor-exam-detail__entry-form">
                <h2>
                  <Msg id={"exam.entryForm"} />
                </h2>
                <CustomFormInput
                  definitions={exam.formDefinitions}
                  fullname={exam.applicant.fullname}
                  fullnameRequired={props.exam.applicantNameRequired}
                  formValues={exam.formValues}
                  onFormChange={onFormChange}
                  examLanguage={multiLangage ? exam.exam.language : undefined}
                  showErrors={hasAttemptedSubmit}
                />
              </div>
            )}
            <ApplicantSegmentSurveyForm
              formValues={exam.applicantSegment}
              showErrors={hasAttemptedSubmit}
              onFormChange={onApplicantSegmentFormChange}
            />
            {exam.isInProgress() && (
              <>
                {formErrorBanner}
                <FormGroup className="code-editor-exam-detail__update-form">
                  <Button
                    onClick={onUpdateEntryForm}
                    size="small"
                    ariaLabel="Update Entry Form"
                  >
                    <Msg id="button.updateForm" />
                  </Button>
                </FormGroup>
              </>
            )}
            {exam.canStart() && !exam.isExpired() && (
              <div>
                {!webcamEnabled && (
                  <FormGroup centered={true}>
                    <Checkbox
                      error={
                        hasAttemptedSubmit && !isTermAccepted
                          ? {
                              agreement: MSG.getMessageByKey(
                                "validation.required",
                              ),
                            }
                          : undefined
                      }
                      name={"agreement"}
                      onChange={onAgreementChange}
                    >
                      <Msg id="editor-exam-detail.agreement" />
                    </Checkbox>
                  </FormGroup>
                )}
                {formErrorBanner}
                <FormGroup
                  className="code-editor-exam-detail__start-button"
                  centered={true}
                >
                  <Button
                    type="primary"
                    size="large"
                    onClick={onOpenStartExam}
                    ariaLabel="Start Exam"
                  >
                    <Msg id="button.startExam" />
                  </Button>
                  {webcamEnabled && (
                    <ModalSteps
                      showModal={isOpenStartExam}
                      onClose={() => setIsOpenStartExam(false)}
                      hasVerifiedLiveness={faceVerified}
                      isWebcamRequired={
                        exam.webcamSettings.livenessVerification ===
                        WebcamSettings.required
                      }
                      onStartExam={() => {
                        onSaveAndStartExam();
                      }}
                      applicantExamId={exam.id + ""}
                      isTermAccepted={isTermAccepted}
                      faceVerified={faceVerified}
                      hasReason={hasReason}
                    />
                  )}
                  {!webcamEnabled && (
                    <Modal
                      title={<Msg id="button.startExam" />}
                      isOpen={isOpenStartExam}
                      onClose={onCloseModal}
                      onClickOk={onSaveAndStartExam}
                      onClickCancel={onCloseModal}
                      okButtonLabel={<Msg id="button.startExam" />}
                      okButtonAriaLabel="Start Exam"
                      ariaLabel="Start Exam"
                    >
                      <Msg id="message.applicant.confirmStartExam" />
                    </Modal>
                  )}
                </FormGroup>
              </div>
            )}
          </div>
        </div>
        <div className="code-editor-exam-detail__aside">
          <HelpSection exam={exam} tutorial={tutorial} sticky />
        </div>
      </div>
      <Footer appType={GlobalAppType.User} />
      <SystemHealth isOpen={isOpenHealth} onClose={onCloseModal} />
    </div>
  );
};

// Memoize the component with a custom comparison function
const areEqual = (
  prevProps: EditorExamDetailProps,
  nextProps: EditorExamDetailProps,
) => {
  return isEqual(prevProps, nextProps);
};

export const EditorExamDetail = React.memo(
  EditorExamDetailFunctional,
  areEqual,
);
